import { useState, useEffect, useContext } from 'react';
import { SocketContext } from '../../context/SocketContext';

export default function OfflineBanner() {
  const [isOffline,   setIsOffline]   = useState(!navigator.onLine);
  const [showInstall, setShowInstall] = useState(false);
  const [syncedCount, setSyncedCount] = useState(0);
  const [dismissed,   setDismissed]   = useState(false);

  // Safe socket access — null if not inside SocketProvider
  const socketCtx      = useContext(SocketContext);
  const isReconnecting = socketCtx?.isReconnecting || false;

  useEffect(() => {
    const on  = () => { setIsOffline(false); setDismissed(false); };
    const off = () => setIsOffline(true);
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      setSyncedCount(e.detail?.synced || 0);
      setTimeout(() => setSyncedCount(0), 4000);
    };
    window.addEventListener('faristaa:queue-synced', handler);
    return () => window.removeEventListener('faristaa:queue-synced', handler);
  }, []);

  useEffect(() => {
    const onReady    = () => setShowInstall(true);
    const onAccepted = () => setShowInstall(false);
    window.addEventListener('faristaa:install-ready',    onReady);
    window.addEventListener('faristaa:install-accepted', onAccepted);
    return () => {
      window.removeEventListener('faristaa:install-ready',    onReady);
      window.removeEventListener('faristaa:install-accepted', onAccepted);
    };
  }, []);

  return (
    <>
      {isOffline && !dismissed && (
        <div className="fixed top-0 inset-x-0 z-[9990] flex items-center justify-between gap-2 px-4 py-2.5 text-white text-xs font-semibold"
          style={{ background: '#ef4444' }}>
          <div className="flex items-center gap-2">
            <i className="fa-solid fa-wifi text-sm" style={{ opacity: 0.7 }} />
            <span>You're offline — some features unavailable</span>
          </div>
          <button onClick={() => setDismissed(true)} className="opacity-70 hover:opacity-100 ml-2">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
      )}

      {!isOffline && isReconnecting && (
        <div className="fixed top-0 inset-x-0 z-[9990] flex items-center justify-center gap-2 px-4 py-2 text-white text-xs font-semibold"
          style={{ background: '#f59e0b' }}>
          <i className="fa-solid fa-spinner animate-spin text-sm" />
          <span>Reconnecting to live updates...</span>
        </div>
      )}

      {syncedCount > 0 && !isOffline && (
        <div className="fixed top-0 inset-x-0 z-[9990] flex items-center justify-center gap-2 px-4 py-2 text-white text-xs font-semibold"
          style={{ background: '#16a34a' }}>
          <i className="fa-solid fa-circle-check text-sm" />
          <span>{syncedCount} pending {syncedCount === 1 ? 'request' : 'requests'} synced!</span>
        </div>
      )}

      {showInstall && !isOffline && (
        <div className="fixed bottom-20 left-4 right-4 z-[9980] bg-white rounded-2xl shadow-2xl border border-border p-4 flex items-center gap-3">
          <img src="/logo192.png" alt="Faristaa" className="w-12 h-12 rounded-xl flex-shrink-0"
            onError={e => { e.target.style.display = 'none'; }} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-text-primary">Install Faristaa</p>
            <p className="text-xs text-text-muted">Add to home screen for instant access</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={() => setShowInstall(false)} className="text-xs text-text-muted px-2 py-1">
              Later
            </button>
            <button onClick={() => { window.showPWAInstallPrompt?.(); setShowInstall(false); }}
              className="text-xs font-bold text-white px-3 py-1.5 rounded-xl"
              style={{ background: '#0D9488' }}>
              Install
            </button>
          </div>
        </div>
      )}
    </>
  );
}
